import {
  PagedResponse,
  PatchWithMeta,
  QueryParams,
  ValidatedResponse,
} from "@ofqual/portal-core/core/types";
import {
  all,
  call,
  delay,
  getContext,
  put,
  select,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";
import { ContactTheRegulator } from "../domain/ContactTheRegulator";
import { COMMS_MODULE_NAME, MODULE_NAME } from "../moduleName";
import {
  contactTheRegulatorService,
  resourceTagsService,
} from "../services";
import {
  ActionPayload,
  LOADING_ITEMS,
  ASSIGNING_ITEM,
  CLOSING_ITEM,
  CREATED_ITEM,
  CREATING_ITEM,
  CREATING_ITEM_VALIDATION_FAIL,
  loadedItem,
  loadingItem,
  LOADING_ITEM,
  SENDING_MESSAGE,
  sendMessageError,
  UPDATING_ITEM,
  UPDATING_ITEM_VALIDATION_FAIL,
  REOPENING_ITEM,
  loadedItems,
} from "./actions";
import { authSelector } from "./selectors";
import { History } from "history";
import { refreshActivityFeed } from "@ofqual/portal-activity-feed";

function* navigate(url: string) {
  const history: History = yield getContext("history");
  yield call(() => history.push(url));
}

function* watchItemsSaga() {
  yield takeLatest(LOADING_ITEMS, itemsSaga);
}

function* watchItemSaga() {
  yield takeLatest(LOADING_ITEM, itemSaga);
}

function* watchUpdateItemSaga() {
  yield takeEvery(UPDATING_ITEM, updateItemSaga);
}
function* watchCreateItemSaga() {
  yield takeEvery(CREATING_ITEM, createItemSaga);
}

function* watchSendMessageSaga() {
  yield takeEvery(SENDING_MESSAGE, sendMessageSaga);
}

function* watchCloseItemeSaga() {
  yield takeEvery(CLOSING_ITEM, closeCtrSaga);
}

function* watchReOpenItemSaga() {
  yield takeLatest(REOPENING_ITEM, reOpenCtrSaga)
}

function* watchAssignItemeSaga() {
  yield takeEvery(ASSIGNING_ITEM, assignItemSaga);
}

function* itemsSaga({ payload }: ActionPayload<QueryParams>) {
  const { token }: ReturnType<typeof authSelector> = yield select(authSelector);

  const items: PagedResponse<ContactTheRegulator> = yield call(
    [contactTheRegulatorService, contactTheRegulatorService.search],
    { ...payload },
    { token }
  );

  yield put(loadedItems(items));
}

function* itemSaga({ payload }: ActionPayload<string>) {
  const { token } = yield select(authSelector);

  const { item, resourceTags } = yield all({
    item: call(
      [contactTheRegulatorService, contactTheRegulatorService.get],
      payload,
      {
        token,
      }
    ),
    resourceTags: call(
      [resourceTagsService, resourceTagsService.get],
      payload,
      {
        token,
      }
    ),
  });
  item.resourceTags = resourceTags;
  yield put(loadedItem(item));
}

function* updateItemSaga({ payload }: ActionPayload<any>) {
  const { token } = yield select(authSelector);
  const { id, patchWithMeta, resourceTagsIds } = payload as {
    id: string;
    patchWithMeta: PatchWithMeta<ContactTheRegulator>;
    resourceTagsIds?: number[];
  };

  const tagsCall = !!resourceTagsIds
    ? call(
        [resourceTagsService, resourceTagsService.post],
        id,
        resourceTagsIds,
        { token }
      )
    : delay(0); // "null" effect

  const { validatedResponse } = yield all({
    validatedResponse: call(
      [contactTheRegulatorService, contactTheRegulatorService.update],
      id,
      patchWithMeta,
      {
        token,
      }
    ),
    tags: tagsCall,
  });

  if (validatedResponse.isValid) {
    yield put(loadingItem(validatedResponse.data.id));
  } else {
    yield put({
      type: UPDATING_ITEM_VALIDATION_FAIL,
      payload: validatedResponse.error,
    });
  }
}

function* createItemSaga({ payload }: ActionPayload<ContactTheRegulator>) {
  const { token } = yield select(authSelector);
  const validatedResponse: ValidatedResponse<ContactTheRegulator> = yield call(
    [contactTheRegulatorService, contactTheRegulatorService.createCtr],
    payload,
    {
      token,
    }
  );
  if (validatedResponse.isValid) {
    yield put({ type: CREATED_ITEM });
    yield call(() =>
      navigate(
        `/${COMMS_MODULE_NAME}/${MODULE_NAME}/${validatedResponse.data.id}`
      )
    );
  } else {
    yield put({
      type: CREATING_ITEM_VALIDATION_FAIL,
      payload: validatedResponse.error,
    });
  }
}

function* sendMessageSaga({
  payload,
}: ActionPayload<{ id: string; files: File[]; message: string }>) {
  const { token } = yield select(authSelector);
  const { id, files, message } = payload;
  try {
    const ctr: ContactTheRegulator = yield call(
      [contactTheRegulatorService, contactTheRegulatorService.sendMessage],
      id,
      files,
      message,
      {
        token,
      }
    );
    yield put(refreshActivityFeed(id));
    yield put(loadedItem(ctr));
  } catch (err: any) {
    if (err?.errorCode === 0) {
      yield put(sendMessageError("This enquiry has been closed."));
    } else {
      throw err;
    }
  }
}

function* closeCtrSaga({ payload }: ActionPayload<any>) {
  const { token } = yield select(authSelector);
  const { id } = payload as {
    id: string;
  };

  const ctr: ContactTheRegulator = yield call(
    [contactTheRegulatorService, contactTheRegulatorService.closeCtr],
    id,
    {
      token,
    }
  );

  yield put(refreshActivityFeed(id));
  yield put(loadedItem(ctr));
}

function* reOpenCtrSaga({ payload }: ActionPayload<{ id: string }>) {
  const { token } = yield select(authSelector);
  const { id } = payload;

  const ctr: ContactTheRegulator = yield call(
    [contactTheRegulatorService, contactTheRegulatorService.reOpenCtr],
    id,
    {
      token
    }
  );

  yield put(refreshActivityFeed(id));
  yield put(loadedItem(ctr));
}

function* assignItemSaga({
  payload,
}: ActionPayload<{
  id: string;
  assignedToUpn: string;
  assignedToDisplayName: string;
}>) {
  const { token } = yield select(authSelector);
  const { id, assignedToUpn, assignedToDisplayName } = payload;

  const ctr: ContactTheRegulator = yield call(
    [contactTheRegulatorService, contactTheRegulatorService.assign],
    id,
    assignedToUpn,
    assignedToDisplayName,
    {
      token,
    }
  );
  yield put(refreshActivityFeed(id));
  yield put(loadedItem(ctr));
}

export function* rootSaga() {
  yield all([
    watchItemsSaga(),
    watchItemSaga(),
    watchUpdateItemSaga(),
    watchCreateItemSaga(),
    watchSendMessageSaga(),
    watchCloseItemeSaga(),
    watchAssignItemeSaga(),
    watchReOpenItemSaga(),
  ]);
}
